import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { FieldType } from '@ngx-formly/material/form-field';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import {
  getFormRoot,
  getValueOnObjectFromStringProperty,
  formCondition,
} from '@intellio/shared/utils';
import { FieldTypeConfig, FormlyModule } from '@ngx-formly/core';
import { MatInputModule } from '@angular/material/input';


@Component({
  selector: 'itc-field-aggregator',
  template: `
@switch (type) {
  @case ('currency') {
    <input
      matInput
      [id]="id"
      [type]="type"
      [readonly]="props.readonly"
      [required]="props.required"
      [errorStateMatcher]="errorStateMatcher"
      [formControl]="control"
      [formlyAttributes]="field"
      [tabIndex]="props.tabindex"
      [placeholder]="props.placeholder"
      prefix="$"
      mask="separator"
      thousandSeparator=","
      />
  }
  @case ('number') {
    <input
      matInput
      [id]="id"
      type="number"
      [readonly]="props.readonly"
      [required]="props.required"
      [errorStateMatcher]="errorStateMatcher"
      [formControl]="control"
      [formlyAttributes]="field"
      [tabIndex]="props.tabindex"
      [placeholder]="props.placeholder"
      />
  }
  @default {
    <input
      matInput
      [id]="id"
      [type]="type || 'text'"
      [readonly]="props.readonly"
      [required]="props.required"
      [errorStateMatcher]="errorStateMatcher"
      [formControl]="control"
      [formlyAttributes]="field"
      [tabIndex]="props.tabindex"
      [placeholder]="props.placeholder"
      />
  }
}
`,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormlyModule, ReactiveFormsModule, MatInputModule],
})
export class FieldAggregatorComponent
  extends FieldType<FieldTypeConfig>
  implements OnInit, OnDestroy
{
  get type() {
    return this.props.type || 'text';
  }

  control: UntypedFormControl;

  product = 0;
  private destroyed$ = new Subject();

  constructor() {
    super();
  }

  ngOnInit(): void {
    this.control = this?.formControl as UntypedFormControl;

    if (this.field.props.aggregateArray) {
      const { path } = this.field.props.aggregateArray;
      const root = getFormRoot(this.field);
      const formControl = this.field.form.root.get(path);

      if (formControl) {
        formControl.valueChanges
          .pipe(takeUntil(this.destroyed$))
          .subscribe(() => {
            this.updateArrayAggregation(this.field, root);
          });
      }
    }

    if (this.field.props.formFieldPaths) {
      this.field.props.formFieldPaths.forEach((element) => {
        const isObject = typeof element === 'object';

        let formControl = null;
        if (isObject) {
          formControl = element.keyIsGlobal
            ? this.field.form.root
            : this.field.form;
        } else {
          formControl = this.field.props.aggregateGlobally
            ? this.field.form.root
            : this.field.form;
        }

        let root = getFormRoot(this.field);
        const model = root.model;
        const result = eval(this.field.props.aggregateCondition);

        this.updateAggregation(this.field, root);

        const el = formControl.get(isObject ? element.key : element);
        const updateValue = () => {
          if (
            !this.field.props.aggregateCondition ||
            (this.field.props.aggregateCondition && result)
          ) {
            this.updateAggregation(this.field, root);
          }
        };

        if (!el) {
          this.field.options?.fieldChanges
            .pipe(
              takeUntil(this.destroyed$),
              filter(
                (change) =>
                  change.type === 'valueChanges' &&
                  change.field?.key ===
                    (isObject ? element.key : element).split('.').pop()
              )
            )
            .subscribe(updateValue);
        } else {
          el.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(updateValue);
        }
      });
    } else {
      //if only aggregating constants, only need to do once
      if (this.field.props.constants) {
        const constantProduct = this.field.props.constants.reduce(
          (sum, value) => {
            return this.calculateAggregation(
              this.field.props.operator,
              sum,
              +value
            );
          },
          this.field.props.operator === 'add' ? 0 : 1
        );
        if (!this.formControl.disabled) {
          this.formControl.setValue(constantProduct);
        }
      }
    }
  }

  updateArrayAggregation(field, root) {
    const { path, fields, itemOperator, arrayOperator } =
      field.props.aggregateArray;
    const arrayValue = getValueOnObjectFromStringProperty(path, root.model);

    if (Array.isArray(arrayValue)) {
      const arrayProduct = arrayValue
        .filter((i) => i != null)
        .reduce(
          (total, item) => {
            const itemProduct = fields.reduce(
              (acc, key) => {
                const val = +item[key] || 0;
                return this.calculateAggregation(itemOperator, acc, val);
              },
              itemOperator === 'multiply' ? 1 : 0
            );

            return this.calculateAggregation(arrayOperator, total, itemProduct);
          },
          arrayOperator === 'multiply' ? 1 : 0
        );

      this.product = arrayProduct;

      if (field.props.constants) {
        const constantProduct = field.props.constants.reduce(
          (sum, value) => {
            return this.calculateAggregation(field.props.operator, sum, +value);
          },
          field.props.operator === 'add' ? 0 : 1
        );
        this.product = this.calculateAggregation(
          field.props.operator,
          this.product,
          constantProduct
        );
      }

      this.applyRounding(field);
    }
  }

  updateAggregation(field, root) {
    const hasValue = field.props.formFieldPaths.some((key) => {
      const isObject = typeof key === 'object';

      if (isObject && key.condition) {
        const conditions = Array.isArray(key.condition)
          ? key.condition
          : [key.condition];
        const result = conditions.every((cond) =>
          formCondition(cond, field, root)
        );
        if (!result) {
          return false;
        }
      }

      let val = null;
      if (isObject) {
        // This handles form field paths that are stored as objects instead of simple strings, objects are primarily used for form fields we conditionally aggregate, or use only for value change re-calculation
        val = key.keyIsGlobal
          ? getValueOnObjectFromStringProperty(key.key, root.model)
          : this.model[key.key];
      } else {
        val = field.props.aggregateGlobally
          ? getValueOnObjectFromStringProperty(key, root.model)
          : this.model[key];
      }

      return val != null;
    });
    let formFieldProduct = 0;
    if (hasValue) {
      formFieldProduct = field.props.formFieldPaths.reduce(
        (sum, key: string | any) => {
          const isObject = typeof key === 'object';

          if (isObject && key.useForValueChangeOnly) {
            return sum;
          }

          if (isObject && key.condition) {
            const conditions = Array.isArray(key.condition)
              ? key.condition
              : [key.condition];
            const result = conditions.every((cond) =>
              formCondition(cond, field, root)
            );
            if (!result) {
              return sum;
            }
          }

          let num = 0;
          if (isObject) {
            num = key.keyIsGlobal
              ? getValueOnObjectFromStringProperty(key.key, root.model)
              : this.model[key.key];
          } else {
            num = field.props.aggregateGlobally
              ? getValueOnObjectFromStringProperty(key, root.model)
              : this.model[key];
          }

          if (num == null && field.props.treatMissingValuesAsZero !== true) {
            return sum;
          }

          return this.calculateAggregation(
            field.props.operator,
            sum,
            num == null ? 0 : +num
          );
        },
        this.field.props.operator === 'add' ? 0 : 1
      );
    }

    if (field.props.constants) {
      const constantProduct = field.props.constants.reduce(
        (sum, value) => {
          return this.calculateAggregation(field.props.operator, sum, +value);
        },
        this.field.props.operator === 'add' ? 0 : 1
      );
      this.product = this.calculateAggregation(
        field.props.operator,
        formFieldProduct,
        constantProduct
      );
    } else {
      this.product = formFieldProduct;
    }

    this.applyRounding(field);
  }

  applyRounding(field) {
    if (field.props.round && !field.props.roundDecimal) {
      // For rounding to the nearest tens, hundreds, thousands, ...
      this.product =
        Math.round(this.product / field.props.round) * field.props.round;
      if (!this.formControl.disabled) {
        this.formControl.setValue(this.product);
      }
    } else {
      if (field.props.type === 'currency') {
        if (!this.formControl.disabled) {
          this.formControl.setValue(
            (Math.round(this.product * 100) / 100).toFixed(2)
          );
          return;
        }
      }

      // For rounding to the nearest tenths, hundreths, thousandths, ...
      if (field.props.roundDecimal) {
        if (!this.formControl.disabled) {
          this.formControl.setValue(
            this.roundToNDigits(this.product, field.props.round)
          );
        }
      } else {
        if (!this.formControl.disabled) {
          this.formControl.setValue(this.roundToNDigits(this.product, 2));
        }
      }
    }
  }

  ngOnDestroy() {
    this.destroyed$.next(null);
    this.destroyed$.complete();
  }

  calculateAggregation(operator: string, accumulator: number, value: number) {
    if (operator === 'multiply') {
      return accumulator * value;
    } else if (operator === 'add') {
      return accumulator + value;
    }
  }

  // Based on this SO Post: https://stackoverflow.com/a/18358056
  roundToNDigits(num: any, precision: any) {
    return +(
      Math.round(Number(num + `e+${precision.toString()}`)) +
      `e-${precision.toString()}`
    );
  }
}
